import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
  Divider,
  useTheme,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import { useGlobalConfig } from 'context/GlobalContext';
import { MigrationApis } from 'apis/migrationAPI';
import { MigrationJob } from 'types/apis/migrationInterfaces';

interface KnownFileState {
  rowIdDesc: File | null;
  boundMatrix: File | null;
  ruleMatrix: File | null;
  metadata: File | null;
  profileInfo: File | null;
  defaultProfileInfo: File | null;
}

interface MigrationUploadFormProps {
  pilotId: string;
  /** From `utility.supportedLocales`; locale dropdown defaults to first entry. */
  supportedLocales?: string[];
  onMigrationStarted: (job: MigrationJob) => void;
  /** Dry-run mode: validate + report only, no import. Hides the Published control and relabels the CTA. */
  dryRun?: boolean;
}

// Must match BE REQUIRED_FILES (migration/run rejects if any are missing after detection)
const REQUIRED_FILES = ['rowIdDesc', 'boundMatrix', 'ruleMatrix', 'metadata'] as const;
// Optional but recommended for full profile defaults (BE OPTIONAL_FILES)
const OPTIONAL_RECOMMENDED = ['defaultProfileInfo'] as const;

// Order matters! defaultProfileInfo before profileInfo (both can contain "profileinfo").
// Align with BE `migration` controller FILE_PATTERNS.
const FILE_PATTERNS: [keyof KnownFileState, RegExp][] = [
  ['rowIdDesc', /row_id_desc/i],
  ['boundMatrix', /bound_matrix/i],
  ['ruleMatrix', /rule_matrix/i],
  ['defaultProfileInfo', /default.*profile/i],
  ['profileInfo', /profileinfo/i],
  ['metadata', /metadata|reco\.xlsx$/i],
];

export const MigrationUploadForm: React.FC<MigrationUploadFormProps> = ({
  pilotId,
  supportedLocales,
  onMigrationStarted,
  dryRun = false,
}) => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const localeOptions = useMemo(() => {
    const raw = Array.isArray(supportedLocales)
      ? supportedLocales.filter((x): x is string => typeof x === 'string' && x.length > 0)
      : [];
    return raw.length > 0 ? raw : ['en'];
  }, [supportedLocales]);

  const [locale, setLocale] = useState<string>(() => localeOptions[0] ?? 'en');
  const [published, setPublished] = useState(true);

  useEffect(() => {
    setLocale((prev) => (localeOptions.includes(prev) ? prev : localeOptions[0] ?? 'en'));
  }, [localeOptions]);

  const [knownFiles, setKnownFiles] = useState<KnownFileState>({
    rowIdDesc: null,
    boundMatrix: null,
    ruleMatrix: null,
    metadata: null,
    profileInfo: null,
    defaultProfileInfo: null,
  });
  const [otherFiles, setOtherFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detectFileType = (file: File): keyof KnownFileState | null => {
    const name = file.name.toLowerCase();
    // Use ordered array to ensure specific patterns match first
    for (const [key, pattern] of FILE_PATTERNS) {
      if (pattern.test(name)) {
        return key;
      }
    }
    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;

    const newKnownFiles = { ...knownFiles };
    const newOtherFiles = [...otherFiles];

    Array.from(selectedFiles).forEach((file) => {
      const type = detectFileType(file);
      if (type) {
        newKnownFiles[type] = file;
      } else {
        // Add to other files if not already present
        if (!newOtherFiles.some(f => f.name === file.name)) {
          newOtherFiles.push(file);
        }
      }
    });

    setKnownFiles(newKnownFiles);
    setOtherFiles(newOtherFiles);
    setError(null);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (key: keyof KnownFileState) => {
    setKnownFiles((prev) => ({ ...prev, [key]: null }));
  };

  const handleRemoveOtherFile = (fileName: string) => {
    setOtherFiles((prev) => prev.filter(f => f.name !== fileName));
  };

  const hasAnyFiles = () => {
    return Object.values(knownFiles).some(f => f !== null) || otherFiles.length > 0;
  };

  const getMissingRequired = () =>
    REQUIRED_FILES.filter((key) => knownFiles[key] === null);

  const getMissingOptionalRecommended = () =>
    OPTIONAL_RECOMMENDED.filter((key) => knownFiles[key] === null);

  const resolveErrorMessage = (err: unknown): string => {
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
      return (err as { message: string }).message;
    }
    return 'Failed to start migration';
  };

  const handleUpload = async () => {
    if (!hasAnyFiles()) return;

    const missingReq = getMissingRequired();
    if (missingReq.length > 0) {
      setError(
        `Missing required files: ${missingReq.join(', ')}. Upload row_id_desc, rule_matrix, bound_matrix, and metadata (or name files so they are auto-detected).`
      );
      return;
    }

    setUploading(true);
    setError(null);

    const resp = await MigrationApis.runMigration(rc, pilotId, {
      locale,
      published,
      dryRun,
      rowIdDesc: knownFiles.rowIdDesc || undefined,
      boundMatrix: knownFiles.boundMatrix || undefined,
      ruleMatrix: knownFiles.ruleMatrix || undefined,
      metadata: knownFiles.metadata || undefined,
      profileInfo: knownFiles.profileInfo || undefined,
      defaultProfileInfo: knownFiles.defaultProfileInfo || undefined,
      otherFiles: otherFiles.length > 0 ? otherFiles : undefined,
    });

    setUploading(false);

    if (resp.data) {
      onMigrationStarted(resp.data as unknown as MigrationJob);
    } else {
      setError(resolveErrorMessage(resp.error));
    }
  };

  const renderFileItem = (key: keyof KnownFileState, label: string, required: boolean) => {
    const file = knownFiles[key];
    return (
      <ListItem
        key={key}
        sx={{
          border: `1px solid ${file ? theme.palette.success.light : theme.palette.divider}`,
          borderRadius: 1,
          mb: 1,
          backgroundColor: file ? `${theme.palette.success.light}15` : 'transparent',
        }}
        secondaryAction={
          file && (
            <IconButton edge="end" onClick={() => handleRemoveFile(key)} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          )
        }
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          {file ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : required ? (
            <WarningIcon color="warning" fontSize="small" />
          ) : (
            <InsertDriveFileIcon color="action" fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2" fontWeight={500}>
              {label} {required && <span style={{ color: theme.palette.error.main }}>*</span>}
            </Typography>
          }
          secondary={file ? file.name : 'Not selected'}
        />
      </ListItem>
    );
  };

  const renderOtherFileItem = (file: File) => {
    return (
      <ListItem
        key={file.name}
        sx={{
          border: `1px solid ${theme.palette.info.light}`,
          borderRadius: 1,
          mb: 1,
          backgroundColor: `${theme.palette.info.light}15`,
        }}
        secondaryAction={
          <IconButton edge="end" onClick={() => handleRemoveOtherFile(file.name)} size="small">
            <DeleteIcon fontSize="small" />
          </IconButton>
        }
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <InsertDriveFileIcon color="info" fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2" fontWeight={500}>
              {file.name}
            </Typography>
          }
          secondary={`${(file.size / 1024).toFixed(1)} KB`}
        />
      </ListItem>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".csv,.xlsx,.json"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {dryRun && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Dry run — sheets are validated against the CMS catalog (categories, appliances, home&#8209;profile attributes).
          {' '}<b>Nothing is imported.</b> You&apos;ll get a report of what maps, what falls back to <code>others</code>, and any blocking issues.
        </Alert>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="migration-locale-label">Locale</InputLabel>
          <Select
            labelId="migration-locale-label"
            label="Locale"
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
          >
            {localeOptions.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {!dryRun && (
          <FormControl fullWidth size="small">
            <InputLabel id="migration-published-label">Published</InputLabel>
            <Select
              labelId="migration-published-label"
              label="Published"
              value={published ? 'true' : 'false'}
              onChange={(e) => setPublished(e.target.value === 'true')}
            >
              <MenuItem value="true">true</MenuItem>
              <MenuItem value="false">false</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          border: `2px dashed ${theme.palette.primary.main}`,
          backgroundColor: `${theme.palette.primary.main}08`,
          '&:hover': { backgroundColor: `${theme.palette.primary.main}15` },
          mb: 3,
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="body1" fontWeight={500}>
          Click to select files or drag and drop
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Supports CSV, XLSX, JSON • Auto-detects file type by name
        </Typography>
      </Paper>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Required files{' '}
        <Typography component="span" variant="caption" color="text.secondary">
          (* required by the API)
        </Typography>
      </Typography>
      <List dense sx={{ mb: 2 }}>
        {renderFileItem('rowIdDesc', 'Row ID Description (row_id_desc…)', true)}
        {renderFileItem('boundMatrix', 'Bound Matrix (bound_matrix…)', true)}
        {renderFileItem('ruleMatrix', 'Rule Matrix (rule_matrix…)', true)}
        {renderFileItem(
          'metadata',
          'Metadata (name contains metadata, or Carrom: ends with reco.xlsx e.g. SK_…_reco.xlsx)',
          true
        )}
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Optional files{' '}
        <Typography component="span" variant="caption" color="text.secondary">
          (recommended: default profile)
        </Typography>
      </Typography>
      <List dense sx={{ mb: 2 }}>
        {renderFileItem('defaultProfileInfo', 'Default Profile (default…profile…)', false)}
        {renderFileItem('profileInfo', 'Profile Info (profileInfo in filename, e.g. …_reco_profileInfo.csv)', false)}
      </List>

      {otherFiles.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Other Files <Typography component="span" variant="caption" color="text.secondary">({otherFiles.length})</Typography>
          </Typography>
          <List dense sx={{ mb: 2 }}>
            {otherFiles.map(file => renderOtherFileItem(file))}
          </List>
        </>
      )}

      {getMissingRequired().length > 0 && hasAnyFiles() && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Missing required files: {getMissingRequired().join(', ')}. Import cannot start until these are present
          (upload directly or use filenames the server can auto-detect).
        </Alert>
      )}

      {getMissingOptionalRecommended().length > 0 && hasAnyFiles() && getMissingRequired().length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Optional: {getMissingOptionalRecommended().join(', ')} — migration can still run without default profile info.
        </Alert>
      )}

      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleUpload}
        disabled={!hasAnyFiles() || uploading}
        startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
      >
        {uploading
          ? (dryRun ? 'Analyzing…' : 'Starting Migration...')
          : (dryRun ? 'Run Analysis (Dry Run)' : 'Start Migration')}
      </Button>
    </Box>
  );
};

